import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLogout, useCurrentUser } from "utils/hooks";
import { deletePracticeSubmissionAction } from "store/actions/submissions";

type DeleteSubmissionProps = {
  submissionId: string | undefined;
};

const useDeletePracticeSubmission = ({
  submissionId,
}: DeleteSubmissionProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common"]);
  const { logOut } = useLogout();
  const { user } = useCurrentUser();

  const [deleting, setDeleting] = useState(false);

  const deletePracticeSubmission = useCallback(() => {
    setDeleting(true);

    try {
      return new Promise(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
                mutation {
                    deleteSubmission(
                        submissionId: "${submissionId}",
                    )
                }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "update_submissions",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (
                error.errors &&
                error.errors[0] &&
                error.errors[0].message.includes("BRKN_6001")
              ) {
                toast.warn(t("errors.login_again_error"));
                setDeleting(false);
                logOut();
              } else {
                toast.error(t("errors.something_went_wrong"));
                setDeleting(false);
                throw new Error(t("errors.failed_error"));
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `${t("errors.something_went_wrong")} ${
                  resData.errors[0].message
                }`
              );
              setDeleting(false);
            } else {
              toast(t("inbox.delete_submission_success"));
              return dispatch(
                deletePracticeSubmissionAction(resData.data.deleteSubmission)
              );
            }
          });
      });
    } catch (error) {
      console.log(error);
      setDeleting(false);
    }
  }, [dispatch, logOut, submissionId, t, user?.activeOrganizationId]);

  return {
    deletePracticeSubmission,
    deleting,
  };
};

export default useDeletePracticeSubmission;
